/*
 * AllRelease.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Container,
  Grid2,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { fetchSubmissionDetails } from "../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchApplicationDetails } from "../../Services/redux/appId-details/AppIdDeatilsActions";
import { fetchAllSubmissions } from "../../Services/redux/ApplicationDetailsSubmissions/ApplicationDetailsSubmissionsActions";
import "./AllRelease.css";
import Topheader from "../../Layout/header/header";
import ApplicationDetailsSideMenu from "../../Components/ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import moment from "moment";
import { visuallyHidden } from "@mui/utils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ApplicationDetailsErrorDialog from "../../Components/ApplicationDetailsErrorDialog/ApplicationDetailsErrorDialog";
import { getConfig } from "../../config";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "asc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function AllRelease() {
  const { appId, submissionId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("fileName");
  const [allSubmissions, setAllSubmissions] = useState([]);
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails
  );
  const errors = useSelector((state) => state.errors);
  const allSubmissionsData = useSelector(
    (state) => state.applicationAllSubmissions.submissions
  );

  // Update the headCells array with width specifications
  const headCells = [
    { id: "fileName", label: "File", sortable: true, width: "15%" },
    { id: "versionCode", label: "Version Code", sortable: true, width: "8%" },
    { id: "versionName", label: "Version Name", sortable: true, width: "8%" },
    { id: "apiLevel", label: "API", sortable: true, width: "5%" },
    {
      id: "sizeInBytes",
      label: "Size",
      numeric: false,
      sortable: true,
      width: "5%",
    },
    { id: "packageName", label: "Package Name", sortable: true, width: "15%" },
    { id: "submittedAt", label: "Submitted", sortable: true, width: "12%" },
    { id: "status", label: "Status", sortable: true, width: "12%" },
    { id: "actions", label: "", sortable: false, width: "5%" },
  ];

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return stableSort(allSubmissions, getComparator(order, orderBy));
  }, [allSubmissions, order, orderBy]);

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = moment(dateString);
    if (!date.isValid()) return "Invalid date";

    return date.format("D MMMM YYYY");
  };

  const sendToSubmission = (row) => {
    if (getConfig().REACT_APP_PORTAL === "domain-approver") {
      navigate(
        `/ui/domain-approver/applications/${appId}/submission/${row.id}`,
        {
          state: {
            teamId: sessionStorage.getItem("teamId"),
            submissionId: row.id,
          },
        }
      );
    } else if (getConfig().REACT_APP_PORTAL === "seller") {
      navigate(`/ui/developer/applications/${appId}`, {
        state: {
          teamId: sessionStorage.getItem("teamId"),
          submissionId: row.id,
        },
      });
    } else if (getConfig().REACT_APP_PORTAL === "global-admin") {
      navigate(`/ui/harman-admin/application/${appId}/submission/${row.id}`);
    }
  };

  useEffect(() => {
    dispatch(fetchApplicationDetails(appId));
  }, [appId]);

  useEffect(() => {
    setAllSubmissions([...allSubmissionsData]);
  }, [allSubmissionsData]);

  useEffect(() => {
    dispatch(fetchSubmissionDetails(appId, submissionId));
    dispatch(fetchAllSubmissions(appId));
  }, [dispatch, appData]);

  const backToApplications = () => {
    if (getConfig().REACT_APP_PORTAL === "seller") {
      navigate(`/ui/developer/applications/${appId}`, {
        state: {
          teamId: sessionStorage.getItem("teamId"),
          submissionId: submissionId,
        },
      });
    } else if (getConfig().REACT_APP_PORTAL === "domain-approver") {
      navigate(
        `/ui/domain-approver/applications/${appId}/submission/${submissionId}`
      );
    } else if (getConfig().REACT_APP_PORTAL === "global-admin") {
      navigate(
        `/ui/harman-admin/application/${appId}/submission/${submissionId}`
      );
    }
  };

  return (
    <>
      <Topheader />
      <Container
        maxWidth="lg"
        sx={{ width: "100% !important", maxWidth: "100% !important" }}
      >
        <Grid2 container>
          <Grid2 size={12}>
            <div className="container-my-release">
              <div
                className="side-menu-my-release"
                style={{ marginTop: "69px" }}
              >
                <button
                  className="arrow-back"
                  onClick={backToApplications}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") backToApplications(); }}
                  type="button"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <ArrowBackIcon style={{ color: " #717273" }} />
                  </div>
                  <div className="my-apps">Back To App Details</div>
                </button>
                {appData && Object.keys(appData).length > 0 && (
                  <ApplicationDetailsSideMenu appData={appData} />
                )}
              </div>
              <div
                className="submission-container-all-release"
                style={
                  submissionData.status === "DRAFT"
                    ? { marginLeft: "0" }
                    : { width: "100%", marginLeft: "22%" }
                }
              >
                <div
                  style={{
                    position: "fixed",
                    backgroundColor: "white",
                    zIndex: "999",
                    width: "82%",
                  }}
                >
                  <div
                    className="listing-header-wrapper-all-release"
                    style={{
                      borderBottom: "1px solid  #12141533",
                      width: "100%",
                    }}
                  >
                    {submissionData &&
                      Object.keys(submissionData).length > 0 &&
                      appData &&
                      Object.keys(appData).length > 0 && (
                        <div className="app-icon-my-release">
                          <div className="icon-name-my-release">
                            <img
                              style={{ borderRadius: "4px" }}
                              src={submissionData.icon.url}
                              alt={submissionData.fileName}
                              width={60}
                              height={60}
                            />
                            {
                              <div className="name-status-my-release">
                                <p className="application-name">
                                  {appData.name}
                                </p>
                              </div>
                            }
                          </div>
                        </div>
                      )}
                  </div>
                  <div className="all-release-body">
                    <p className="all-release-text">
                      All Release{" "}
                      <span className="release-text-all-release">
                        {" "}
                        - Releases with the binaries added are shown here
                      </span>
                    </p>
                  </div>
                </div>
                <div style={{ marginTop: "175px" }}>
                  {allSubmissions && allSubmissions.length > 0 && (
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "auto",
                      }}
                    >
                      <Table
                        sx={{
                          borderCollapse: "separate",
                          borderSpacing: 0,
                          minWidth: "100%",
                          tableLayout: "fixed",
                        }}
                      >
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: "#E8E8E8",
                              "& th": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            {headCells.map((headCell) => (
                              <TableCell
                                key={headCell.id}
                                align={headCell.numeric ? "right" : "left"}
                                sortDirection={
                                  orderBy === headCell.id ? order : false
                                }
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "550",
                                  lineHeight: "14px",
                                  letterSpacing: "0.4px",
                                  textAlign: "left",
                                  border: "1px solid #12141533",
                                  padding: "8px 4px !important",
                                  width: headCell.width,
                                  maxWidth: headCell.width,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {headCell.sortable ? (
                                  <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={
                                      orderBy === headCell.id ? order : "asc"
                                    }
                                    onClick={(event) =>
                                      handleRequestSort(event, headCell.id)
                                    }
                                    sx={{
                                      fontSize: "12px",
                                      "& .MuiTableSortLabel-icon": {
                                        fontSize: "14px",
                                      },
                                    }}
                                  >
                                    {headCell.label}
                                    {orderBy === headCell.id ? (
                                      <span style={visuallyHidden}>
                                        {order === "desc"
                                          ? "sorted descending"
                                          : "sorted ascending"}
                                      </span>
                                    ) : null}
                                  </TableSortLabel>
                                ) : (
                                  headCell.label
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((row) => (
                            <TableRow
                              key={row.name}
                              style={{ cursor: "pointer" }}
                              onClick={() => sendToSubmission(row)}
                            >
                              {/* File Name */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "15%",
                                  maxWidth: "15%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.fileName}
                              </TableCell>

                              {/* Version Code */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "8%",
                                  maxWidth: "8%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.versionCode}
                              </TableCell>

                              {/* Version Name */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "8%",
                                  maxWidth: "8%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.versionName}
                              </TableCell>

                              {/* API Level */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "5%",
                                  maxWidth: "5%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {row.apiLevel}
                              </TableCell>

                              {/* Size */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "5%",
                                  maxWidth: "5%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {Math.floor(row.sizeInBytes / (1024 * 1024))} MB
                              </TableCell>

                              {/* Package Name */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "15%",
                                  maxWidth: "15%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={row.packageName}
                              >
                                {row.packageName}
                              </TableCell>

                              {/* Submitted Date */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "6px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "12%",
                                  maxWidth: "12%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {formatDate(row.submittedAt)}
                              </TableCell>

                              {/* Status */}
                              <TableCell
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: "500",
                                  letterSpacing: "0.1px",
                                  textAlign: "left",
                                  color: "#2A2C2D",
                                  padding: "2px 4px !important",
                                  border: "1px solid #12141533",
                                  width: "10%",
                                  maxWidth: "10%",
                                  overflow: "hidden",
                                }}
                              >
                                <Chip
                                  sx={{
                                    backgroundColor: () => {
                                      switch (row.status) {
                                        case "APPROVED_FOR_PRODUCTION":
                                          return "#9ccaf6";
                                        case "SUBMIT_FOR_PRODUCTION":
                                          return "#9ccaf6";
                                        case "PUBLISHED":
                                          return "#98f0c8";
                                        case "DRAFT":
                                          return "#e8e8e8";
                                        case "REJECTED_FOR_PRODUCTION":
                                          return "#fbc2bc";
                                        case "APPROVED_FOR_BETA":
                                          return "#9ccaf6";
                                        case "APPROVED_FOR_STAGING":
                                          return "#9ccaf6";
                                        case "REJECTED_FOR_STAGING":
                                          return "#fbc2bc";
                                        default:
                                          return "#e8e8e8";
                                      }
                                    },
                                    color: () => {
                                      switch (row.status) {
                                        case "APPROVED_FOR_PRODUCTION":
                                          return "#02315d";
                                        case "SUBMIT_FOR_PRODUCTION":
                                          return "#02315d";
                                        case "PUBLISHED":
                                          return "#00562d";
                                        case "DRAFT":
                                          return "#2a2c2d";
                                        case "REJECTED_FOR_PRODUCTION":
                                          return "#b50b00";
                                        case "APPROVED_FOR_BETA":
                                          return "#02315d";
                                        case "APPROVED_FOR_STAGING":
                                          return "#02315d";
                                        case "REJECTED_FOR_STAGING":
                                          return "#b50b00";
                                        default:
                                          return "#2a2c2d";
                                      }
                                    },

                                    fontSize: "10px",
                                    fontWeight: "600",
                                    lineHeight: "12px",
                                    padding: "2px 6px !important",
                                    height: "24px",
                                    maxWidth: "100%",
                                  }}
                                  title={row.status}
                                  className="chip-status"
                                  label={row.status}
                                  component="span"
                                />
                              </TableCell>

                              {/* Actions */}
                              <TableCell
                                sx={{
                                  border: "1px solid #12141533",
                                  textAlign: "center",
                                  padding: "4px !important",
                                  width: "5%",
                                  maxWidth: "5%",
                                }}
                              >
                                <IconButton
                                  component="a"
                                  href={row.apkUrl}
                                  sx={{
                                    color: "grey",
                                    borderRadius: "4px",
                                    height: "28px",
                                    width: "28px",
                                    padding: "0px !important",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 32 32"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M16 20L11 15L12.4 13.55L15 16.15V8H17V16.15L19.6 13.55L21 15L16 20ZM10 24C9.45 24 8.97917 23.8042 8.5875 23.4125C8.19583 23.0208 8 22.55 8 22V19H10V22H22V19H24V22C24 22.55 23.8042 23.0208 23.4125 23.4125C23.0208 23.8042 22.55 24 22 24H10Z"
                                      fill="#121415"
                                      fillOpacity="0.6"
                                    />
                                  </svg>
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </div>
              </div>
            </div>
            {errors?.errorMessage != null &&
              errors?.errorType != null && <ApplicationDetailsErrorDialog />}
          </Grid2>
        </Grid2>
      </Container>
    </>
  );
}

export default AllRelease;
